import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { IS_NATIVE } from "../lib/stripeFlow";
import type { IgVerifyStatus } from "../lib/instagramLogin";
import { hasPendingInstagramSignup } from "../hooks/useOnboardingState";
import "./StripeCallbackPage.css";
import { t as tr } from "../i18n";

// Where the "Continue with Instagram" round trip lands (the server redirects
// every outcome here — lib/identity/instagram.js). This page decides nothing:
// it carries the status to the screen the talent came from, which shows the
// one line that fits. Three ways it renders, and none of them is a dead end:
//
//   1. Web: straight on to the QR tab (or the profile drawer) with ?igVerify=…
//      — or, for a talent still signing up (to=signup, build 54), back to the
//      Connect Instagram step with ?igSignup=…
//   2. In the app, universal link fired: same thing, in-app.
//   3. In a plain browser with app=1 (the universal link did not fire): the
//      "Return to Request App" card, the same hand-off the Stripe return uses.
const KNOWN: IgVerifyStatus[] = ["done", "cancelled", "expired", "taken", "personal", "failed"];

const InstagramCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const raw = (searchParams.get("status") || "failed") as IgVerifyStatus;
  const status: IgVerifyStatus = KNOWN.includes(raw) ? raw : "failed";
  const toParam = searchParams.get("to");
  // A sign-up has no account to land in. An older website page in the app's
  // sheet hands back to=qr instead of signup; a signed-out phone whose saved
  // sign-up waits on Instagram is that sign-up all the same.
  const to =
    toParam === "signup" || (toParam !== "profile" && IS_NATIVE && !user && hasPendingInstagramSignup())
      ? "signup"
      : toParam === "profile"
        ? "profile"
        : "qr";

  const [appHandoff, setAppHandoff] = useState<boolean>(
    () => !IS_NATIVE && searchParams.get("app") === "1"
  );

  // The same page inside the app: DeepLinkHandler routes the custom scheme here.
  const handoffUrl = useMemo(
    () => `requestapp://instagram/callback?status=${status}&to=${to}`,
    [status, to]
  );

  // Back to where they tapped the button. The QR tab needs the slug; if we
  // don't have it yet (a cold web session), the Requests tab is the safe
  // landing — never a blank screen.
  const destination = useMemo(() => {
    if (to === "signup") return `/register?igSignup=${status}`;
    if (to === "profile") return `/requests?igVerify=${status}#profile`;
    return user?.slug ? `/qr/${user.slug}?igVerify=${status}` : `/requests?igVerify=${status}`;
  }, [to, status, user?.slug]);

  useEffect(() => {
    if (!appHandoff) return;
    // Best-effort auto-open: iOS asks "Open in Request App?". If it needs a
    // tap, the button below is the path.
    const t = setTimeout(() => {
      window.location.href = handoffUrl;
    }, 400);
    return () => clearTimeout(t);
  }, [appHandoff, handoffUrl]);

  useEffect(() => {
    if (appHandoff) return;
    navigate(destination, { replace: true });
  }, [appHandoff, destination, navigate]);

  if (!appHandoff) return <p className="stripe-status">{tr("instagramCallbackPage.oneMoment")}</p>;

  return (
    <div className="stripe-callback-container">
      <div className="stripe-box stripe-incomplete-card">
        <h2 className="stripe-ic-title">
          {status === "done" ? tr("instagramCallbackPage.instagramVerified") : tr("instagramCallbackPage.almostThere")}
        </h2>
        <p className="stripe-ic-message">{tr("instagramCallbackPage.headBackToThe")}</p>
        <a className="stripe-connect-button" href={handoffUrl} style={{ textDecoration: "none" }}>
          <span className="stripe-connect-label">{tr("instagramCallbackPage.returnToRequestApp")}</span>
          <svg
            className="stripe-connect-chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </a>
        <p style={{ marginTop: 16, fontSize: "0.85rem" }}>
          <button
            type="button"
            className="stripe-link"
            onClick={() => setAppHandoff(false)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit" }}
          >
            {tr("instagramCallbackPage.continueInTheBrowser")}
          </button>
        </p>
      </div>
    </div>
  );
};

export default InstagramCallbackPage;
